import { supabase } from '../lib/supabase'
import type { VersionEntry } from '../types/portal'
import { resolveProjectId } from './projectWrites'

/**
 * Stored version history.
 *
 * Every published version of a deliverable is a row in `deliverable_versions`
 * — including the current one, so its publication date is permanent rather
 * than session-only, and so the Drive link a version was reviewed on survives
 * the next publish.
 *
 * A publish is NOT performed field by field from the browser. It goes through
 * the `publish_deliverable_version` RPC, which reads the currently stored
 * version and its links, archives them, and only then writes the new version —
 * all in one transaction. That is what guarantees V3's link is preserved even
 * though staff have already typed V4's link into the form, and that a failed
 * publish cannot leave a half-finished transition.
 */

export interface StoredVersions {
  /** Superseded versions, newest first — what Version History lists. */
  history: VersionEntry[]
  /** The current version's publication date, or '' if it was never recorded. */
  currentDate: string
}

function warn(what: string, error: { message?: string; code?: string } | null | unknown) {
  if (!error) return
  const detail = error as { message?: string; code?: string }
  console.warn(
    `[supabase] ${what} failed: ${detail.message ?? String(error)}${
      detail.code ? ` (${detail.code})` : ''
    }`,
  )
}

/** "12 Sep 2026", or '—' where no date was ever recorded. */
function dateLabel(iso: unknown): string {
  const value = iso === null || iso === undefined ? '' : String(iso)
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Every stored version for a project, keyed by deliverable key.
 *
 * `deliverableKeys` maps the database's deliverable id to the key the
 * interface uses, so callers that already loaded the deliverables do not pay
 * for a second lookup.
 */
export async function fetchVersionHistory(
  projectId: string,
  deliverableKeys: Map<string, string>,
): Promise<Map<string, StoredVersions>> {
  const found = new Map<string, StoredVersions>()
  if (!supabase || !projectId) return found

  const { data, error } = await supabase
    .from('deliverable_versions')
    .select(
      'id, deliverable_id, version, label, drive_url, audio_url, visible_to_client, is_current, published_at, change_count',
    )
    .eq('project_id', projectId)
    .order('published_at', { ascending: false, nullsFirst: false })

  warn('version history fetch', error)
  if (!data?.length) return found

  for (const row of data as Record<string, unknown>[]) {
    const key = deliverableKeys.get(String(row.deliverable_id))
    if (!key) continue

    const entry = found.get(key) ?? { history: [], currentDate: '' }

    if (row.is_current) {
      /* The current version's own publication date — the field that used to
         live only in this browser. */
      const label = dateLabel(row.published_at)
      entry.currentDate = label === '—' ? '' : label
    } else {
      entry.history.push({
        id: String(row.id),
        version: String(row.version ?? ''),
        label: String(row.label ?? '') || 'Superseded',
        date: dateLabel(row.published_at),
        /* Never invented: a version published before this was recorded has no
           link, and is shown as such rather than borrowing a newer one. */
        driveUrl: row.drive_url === null || row.drive_url === undefined ? '' : String(row.drive_url),
        changeCount: typeof row.change_count === 'number' ? row.change_count : undefined,
        visibleToClient: Boolean(row.visible_to_client),
      })
    }

    found.set(key, entry)
  }

  return found
}

export interface PublishResult {
  ok: boolean
  error?: string
}

const PUBLISH_ERROR = 'Version could not be published. Nothing was changed.'

/** Database errors that describe a real conflict, not a transport failure. */
const PUBLISH_ERRORS: Record<string, string> = {
  P0409: 'This deliverable was changed by another staff member. Reload before publishing.',
  P0410: 'This version already exists. Publish a new version number.',
  /* The deliverable row the key refers to does not exist in this project. */
  P0404: 'This deliverable is not in the database yet. Reload the project and try again.',
  /* Publishing is refused by row-level security for this account. */
  '42501': 'Your account is not permitted to publish versions. Check Portal staff access.',
  /* The installed function does not match what is being called. */
  PGRST202:
    'The publish function is not available in the database. Run the deliverable_versions migration.',
}

/**
 * Publishes a new version through one transactional database operation.
 *
 * The RPC does the whole transition: it locks the deliverable, archives the
 * version it currently holds together with the links stored against it,
 * records the new version as current with its publication time, and updates
 * the deliverable. Nothing here reconstructs the old version from form state,
 * and no link is written to the deliverable beforehand — that is what keeps
 * V3's link on V3 when V4 is published.
 *
 * A refusal is never retried: the caller is told, and nothing was changed.
 */
export async function publishVersionRow(input: {
  projectId: string
  deliverableKey: string
  version: string
  /**
   * The version that was loaded for this deliverable before the staff member
   * began publishing. '' for a deliverable that has no version yet, which is
   * the only way V1 can be published. Never derived from edited local state.
   */
  expectedCurrentVersion: string
  driveUrl: string
  audioUrl: string
  /** Used to recover the real project id if the interface still holds a local one. */
  projectSlug: string
}): Promise<PublishResult> {
  if (!supabase) return { ok: true }

  /**
   * The RPC takes a uuid. A project created moments ago may still be held
   * under a temporary local id, which the database would reject outright, so
   * the real id is resolved first rather than sent as-is.
   */
  const projectId = await resolveProjectId(input.projectId, input.projectSlug)
  if (!projectId) {
    return { ok: false, error: 'This project could not be found in the database.' }
  }

  const { error } = await supabase.rpc('publish_deliverable_version', {
    p_project_id: projectId,
    p_deliverable_key: input.deliverableKey,
    p_version: input.version,
    p_expected_current_version: input.expectedCurrentVersion,
    p_drive_url: input.driveUrl || null,
    p_audio_url: input.audioUrl || null,
  })

  if (error) {
    console.error('[supabase] publish_deliverable_version failed', error)

    const code = (error as { code?: string }).code ?? ''

    if (PUBLISH_ERRORS[code]) return { ok: false, error: PUBLISH_ERRORS[code] }

    /* P0411 explains which sequential version number is required, and P0400
       names a missing argument, so the database's own message is the useful
       one. */
    if (code === 'P0411' || code === 'P0400') {
      return { ok: false, error: error.message || PUBLISH_ERROR }
    }

    /**
     * Anything else is reported with the exact database message and code. A
     * generic sentence hides the one piece of information needed to fix it.
     */
    const detail = `${error.message ?? ''}${code ? ` (${code})` : ''}`.trim()
    return { ok: false, error: detail ? `${PUBLISH_ERROR} ${detail}` : PUBLISH_ERROR }
  }
  return { ok: true }
}

/** Whether an older version is shared with the client. Addressed by row id. */
export async function setVersionVisibilityRow(
  versionId: string,
  visible: boolean,
): Promise<void> {
  if (!supabase) return
  const { error } = await supabase
    .from('deliverable_versions')
    .update({ visible_to_client: visible })
    .eq('id', versionId)
  warn('version visibility', error)
}

/**
 * Keeps the current version's stored links in step when staff correct them
 * without publishing. Only ever touches the row marked current, so an
 * archived version's link can never be overwritten.
 */
export async function updateCurrentVersionLinks(
  projectId: string,
  deliverableKey: string,
  links: { driveUrl?: string; audioUrl?: string },
): Promise<void> {
  if (!supabase) return

  const { data, error } = await supabase
    .from('deliverables')
    .select('id')
    .eq('project_id', projectId)
    .eq('deliverable_key', deliverableKey)
    .maybeSingle()
  warn('current version lookup', error)
  if (!data?.id) return

  const patch: Record<string, unknown> = {}
  if (links.driveUrl !== undefined) patch.drive_url = links.driveUrl || null
  if (links.audioUrl !== undefined) patch.audio_url = links.audioUrl || null
  if (Object.keys(patch).length === 0) return

  const { error: updateError } = await supabase
    .from('deliverable_versions')
    .update(patch)
    .eq('deliverable_id', data.id)
    .eq('is_current', true)
  warn('current version links', updateError)
}
